<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NButton, NCard, NBadge, NTag } from 'naive-ui'
import {
    LANGUAGES, getCurrentLanguage, setCurrentLanguage,
    getLocaleFromPreferred
} from '@/services/language'
import { dt } from '@/utils/i18n'
import patientDashboardImage from '@/assets/images/campaign-creators-pypeCEaJeZY-unsplash.jpg' // You'll need this
import encryptionImage from '@/assets/images/marek-studzinski-iv-Iau6sLgw-unsplash.jpg'
import documentImage from '@/assets/images/medical-563427_640.jpg'
import sharingImage from '@/assets/images/vitaly-gariev-8WYkI3cEZm8-unsplash.jpg'

const router = useRouter()
const { t, locale } = useI18n()

onMounted(() => {
    locale.value = getLocaleFromPreferred(getCurrentLanguage())
})

const handlePatientRegister = () => {
    router.push({
        name: 'register',
        query: { role: 'patient' }
    })
}

const handlePatientLogin = () => {
    router.push({
        name: 'login',
        query: { role: 'patient' }
    })
}
</script>

<template>
    <div class="patient-page">
        <!-- Hero Section for Patients -->
        <section class="patient-hero">
            <div class="container">
                <div class="hero-grid">
                    <div class="hero-content">
                        <h1 class="hero-title">{{ dt('patientsHome.heroTitle') }}</h1>
                        <p class="hero-subtitle">{{ dt('patientsHome.heroSubtitle') }}</p>

                        <div class="cta-buttons">
                            <NButton type="primary" size="large" class="cta-button-primary"
                                @click="handlePatientRegister">
                                {{ dt('patientsHome.getStarted') }}
                            </NButton>
                            <NButton size="large" class="cta-button-secondary" @click="handlePatientLogin">
                                {{ dt('patientsHome.patientLogin') }}
                            </NButton>
                        </div>

                        <div class="hero-stats">
                            <div class="stat-item">
                                <div class="stat-number">100%</div>
                                <div class="stat-label">{{ dt('patientsHome.stats.encrypted') }}</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-number">0</div>
                                <div class="stat-label">{{ dt('patientsHome.stats.plaintext') }}</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-number">12</div>
                                <div class="stat-label">{{ dt('patientsHome.stats.languages') }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="hero-image">
                        <img :src="patientDashboardImage" alt="Patient Dashboard" class="dashboard-preview" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Security First Section -->
        <section class="security-first">
            <div class="container">
                <h2 class="section-title">{{ dt('patientsHome.securityTitle') }}</h2>
                <div class="security-grid">
                    <div class="security-card">
                        <div class="security-icon">🔐</div>
                        <h3>{{ dt('patientsHome.security.masterPassword.title') }}</h3>
                        <p>{{ dt('patientsHome.security.masterPassword.description') }}</p>
                    </div>
                    <div class="security-card">
                        <div class="security-icon">🔒</div>
                        <h3>{{ dt('patientsHome.security.e2e.title') }}</h3>
                        <p>{{ dt('patientsHome.security.e2e.description') }}</p>
                    </div>
                    <div class="security-card">
                        <div class="security-icon">👁️‍🗨️</div>
                        <h3>{{ dt('patientsHome.security.zeroKnowledge.title') }}</h3>
                        <p>{{ dt('patientsHome.security.zeroKnowledge.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Features for Patients -->
        <section class="patient-features">
            <div class="container">
                <h2 class="section-title">{{ dt('patientsHome.featuresTitle') }}</h2>

                <!-- Feature 1: End-to-End Encryption -->
                <div class="feature-showcase">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('patientsHome.features.encryption.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('patientsHome.features.encryption.item1') }}</li>
                            <li>✓ {{ dt('patientsHome.features.encryption.item2') }}</li>
                            <li>✓ {{ dt('patientsHome.features.encryption.item3') }}</li>
                            <li>✓ {{ dt('patientsHome.features.encryption.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <img :src="encryptionImage" alt="End-to-End Encryption" />
                    </div>
                </div>

                <!-- Feature 2: Document Storage -->
                <div class="feature-showcase reverse">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('patientsHome.features.documents.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('patientsHome.features.documents.item1') }}</li>
                            <li>✓ {{ dt('patientsHome.features.documents.item2') }}</li>
                            <li>✓ {{ dt('patientsHome.features.documents.item3') }}</li>
                            <li>✓ {{ dt('patientsHome.features.documents.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <img :src="documentImage" alt="Secure Document Storage" />
                    </div>
                </div>

                <!-- Feature 3: Secure Sharing -->
                <div class="feature-showcase">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('patientsHome.features.sharing.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('patientsHome.features.sharing.item1') }}</li>
                            <li>✓ {{ dt('patientsHome.features.sharing.item2') }}</li>
                            <li>✓ {{ dt('patientsHome.features.sharing.item3') }}</li>
                            <li>✓ {{ dt('patientsHome.features.sharing.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <div class="coming-soon-badge">
                            <NBadge value="Coming Soon" type="warning" />
                        </div>
                        <img :src="sharingImage" alt="Secure Profile Sharing" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Why Choose Our Platform -->
        <section class="why-choose">
            <div class="container">
                <h2 class="section-title">{{ dt('patientsHome.whyChooseTitle') }}</h2>
                <div class="benefits-grid">
                    <div class="benefit-card">
                        <div class="benefit-icon">🛡️</div>
                        <h3>{{ dt('patientsHome.benefits.privacy.title') }}</h3>
                        <p>{{ dt('patientsHome.benefits.privacy.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">🗣️</div>
                        <h3>{{ dt('patientsHome.benefits.multilingual.title') }}</h3>
                        <p>{{ dt('patientsHome.benefits.multilingual.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">📱</div>
                        <h3>{{ dt('patientsHome.benefits.mobile.title') }}</h3>
                        <p>{{ dt('patientsHome.benefits.mobile.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">🔗</div>
                        <h3>{{ dt('patientsHome.benefits.sharing.title') }}</h3>
                        <p>{{ dt('patientsHome.benefits.sharing.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">💼</div>
                        <h3>{{ dt('patientsHome.benefits.organization.title') }}</h3>
                        <p>{{ dt('patientsHome.benefits.organization.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">⚡</div>
                        <h3>{{ dt('patientsHome.benefits.instant.title') }}</h3>
                        <p>{{ dt('patientsHome.benefits.instant.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- How It Works for Patients -->
        <section class="how-it-works-patients">
            <div class="container">
                <h2 class="section-title">{{ dt('patientsHome.howItWorksTitle') }}</h2>
                <div class="steps-grid">
                    <div class="step-card">
                        <div class="step-number">1</div>
                        <h4>{{ dt('patientsHome.steps.register.title') }}</h4>
                        <p>{{ dt('patientsHome.steps.register.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">2</div>
                        <h4>{{ dt('patientsHome.steps.setup.title') }}</h4>
                        <p>{{ dt('patientsHome.steps.setup.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">3</div>
                        <h4>{{ dt('patientsHome.steps.upload.title') }}</h4>
                        <p>{{ dt('patientsHome.steps.upload.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">4</div>
                        <h4>{{ dt('patientsHome.steps.share.title') }}</h4>
                        <p>{{ dt('patientsHome.steps.share.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Language Coverage -->
        <section class="languages">
            <div class="container">
                <h2 class="section-title">{{ dt('patientsHome.languagesTitle') }}</h2>
                <p class="languages-subtitle">{{ dt('patientsHome.languagesSubtitle') }}</p>
                <div class="language-tags">
                    <NTag v-for="lang in LANGUAGES" :key="lang.value" type="success" size="large" class="lang-tag">
                        {{ lang.label }}
                    </NTag>
                </div>
            </div>
        </section>

        <!-- Final CTA -->
        <section class="final-cta">
            <div class="container">
                <div class="cta-content">
                    <h2>{{ dt('patientsHome.finalCta.title') }}</h2>
                    <p>{{ dt('patientsHome.finalCta.subtitle') }}</p>
                    <div class="cta-buttons">
                        <NButton type="primary" size="large" class="cta-button-large" @click="handlePatientRegister">
                            {{ dt('patientsHome.finalCta.button') }}
                        </NButton>
                    </div>
                    <p class="cta-note">{{ dt('patientsHome.finalCta.note') }}</p>
                </div>
            </div>
        </section>

        <!-- For Providers Link -->
        <section class="for-providers-link">
            <div class="container">
                <p>{{ dt('patientsHome.lookingToJoin') }}
                    <router-link to="/providers" class="provider-link">{{ dt('patientsHome.goToProviderSite') }}</router-link>
                </p>
            </div>
        </section>
    </div>
</template>

<style scoped>
.patient-page {
    min-height: 100vh;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

/* Patient-themed Hero */
.patient-hero {
    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
    /* Blue gradient for patients */
    color: white;
    padding: 5rem 0;
}

.hero-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
}

.hero-content {
    text-align: left;
}

.hero-title {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
}

.hero-subtitle {
    font-size: 1.4rem;
    margin-bottom: 2.5rem;
    opacity: 0.95;
    line-height: 1.6;
}

.cta-buttons {
    display: flex;
    gap: 1rem;
    margin-bottom: 3rem;
}

.cta-button-primary {
    background: #e74c3c !important;
    /* Red accent for patients */
    border: none;
    padding: 0 2.5rem;
    font-size: 1.1rem;
}

.cta-button-secondary {
    background: transparent;
    border: 2px solid white;
    color: white;
    padding: 0 2.5rem;
    font-size: 1.1rem;
}

.cta-button-secondary:hover {
    background: rgba(255, 255, 255, 0.1);
}

.hero-stats {
    display: flex;
    gap: 3rem;
}

.stat-item {
    text-align: center;
}

.stat-number {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.stat-label {
    font-size: 0.9rem;
    opacity: 0.9;
}

.dashboard-preview {
    max-width: 100%;
    border-radius: 15px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Security First Section */
.security-first {
    padding: 5rem 0;
    background: #f8f9fa;
}

.section-title {
    text-align: center;
    font-size: 2.8rem;
    margin-bottom: 1rem;
    color: #3498db;
}

.security-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.security-card {
    background: white;
    padding: 2.5rem;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border-top: 4px solid #3498db;
}

.security-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(52, 152, 219, 0.2);
}

.security-icon {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
}

.security-card h3 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.4rem;
}

.security-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* Patient Features */
.patient-features {
    padding: 5rem 0;
}

.feature-showcase {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    margin: 4rem 0;
}

.feature-showcase.reverse {
    direction: rtl;
}

.feature-showcase.reverse .feature-content {
    direction: ltr;
}

.feature-title {
    font-size: 2rem;
    color: #3498db;
    margin-bottom: 1.5rem;
}

.feature-list {
    list-style: none;
    padding: 0;
}

.feature-list li {
    padding: 0.8rem 0;
    font-size: 1.1rem;
    color: #4a5568;
    border-bottom: 1px solid #e2e8f0;
}

.feature-image {
    position: relative;
}

.feature-image img {
    max-width: 100%;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.coming-soon-badge {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
}

/* Why Choose Section */
.why-choose {
    padding: 5rem 0;
    background: #f8f9fa;
}

.benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.benefit-card {
    background: white;
    padding: 2.5rem;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border-top: 4px solid #3498db;
}

.benefit-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(52, 152, 219, 0.2);
}

.benefit-icon {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
}

.benefit-card h3 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.4rem;
}

.benefit-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* How It Works for Patients */
.how-it-works-patients {
    padding: 5rem 0;
    background: linear-gradient(135deg, #f6f9fc 0%, #edf2f7 100%);
}

.steps-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.step-card {
    background: white;
    text-align: center;
    padding: 2.5rem;
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.step-number {
    width: 50px;
    height: 50px;
    background: #3498db;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    font-size: 1.5rem;
    font-weight: bold;
}

.step-card h4 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.3rem;
}

.step-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* Languages Section */
.languages {
    padding: 5rem 0;
    background: #f8f9fa;
}

.languages-subtitle {
    text-align: center;
    font-size: 1.2rem;
    color: #7f8c8d;
    margin-bottom: 2rem;
}

.language-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    max-width: 800px;
    margin: 0 auto;
}

.lang-tag {
    font-size: 1.1rem;
}

/* Final CTA */
.final-cta {
    background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
    padding: 80px 20px;
    text-align: center;
}

.final-cta .container {
    max-width: 1200px;
    margin: 0 auto;
}

.final-cta .cta-content {
    max-width: 800px;
    margin: 0 auto;
}

.final-cta h2 {
    font-size: 2.5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 1rem;
    text-align: center;
}

.final-cta p {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 2rem;
    text-align: center;
}

.cta-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 0;
}

.cta-button-large {
    padding: 16px 48px !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    background-color: #e74c3c !important;
    /* Red for patients */
    border: none !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s ease !important;
    cursor: pointer;
}

.cta-button-large:hover {
    background-color: #c0392b !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
}

.cta-note {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 1rem;
    text-align: center;
}

/* For Providers Link */
.for-providers-link {
    padding: 2rem 0;
    text-align: center;
    background: #2c3e50;
    color: white;
}

.provider-link {
    color: #3498db;
    text-decoration: underline;
    font-weight: 600;
}

.provider-link:hover {
    color: #2980b9;
}

/* Responsive */
@media (max-width: 768px) {
    .final-cta {
        padding: 60px 20px;
    }

    .final-cta h2 {
        font-size: 2rem;
    }

    .final-cta p {
        font-size: 1.1rem;
    }

    .cta-button-large {
        padding: 14px 32px !important;
        font-size: 16px !important;
        width: 100%;
        max-width: 300px;
    }

    .hero-grid,
    .feature-showcase,
    .feature-showcase.reverse {
        grid-template-columns: 1fr;
    }

    .hero-title {
        font-size: 2.2rem;
    }

    .cta-buttons {
        flex-direction: column;
    }

    .hero-stats {
        flex-direction: column;
        gap: 1.5rem;
    }

    .benefits-grid,
    .security-grid {
        grid-template-columns: 1fr;
    }
}
</style>